/**
 * Numbering form defaults — shared between the route (hydrate + diff)
 * and the form (render). One state object holds the live values for all
 * V1.0 doc types; the save handler iterates and dispatches one
 * `numbering.update` mutation per dirty card.
 *
 * Padding is constrained to 3..6 (mirrors the wire enum + the DB CHECK).
 */
import type { z } from "zod";
import type { numbering } from "@flint/api-contract";

type GetOutput = z.infer<typeof numbering.getOutput>;
type SettingsRow = GetOutput["rows"][number];

export type PaddingValue = 3 | 4 | 5 | 6;

export interface NumberingCardValues {
  documentType: string;
  label: string;
  prefix: string;
  padding: PaddingValue;
  nextSeq: number;
  poolId: string | null;
  canSetNextSeq: boolean;
}

/** Keyed by documentType for O(1) per-card updates. */
export type NumberingFormState = Record<string, NumberingCardValues>;

export function rowsToState(rows: ReadonlyArray<SettingsRow>): NumberingFormState {
  const out: NumberingFormState = {};
  for (const r of rows) {
    out[r.documentType] = {
      documentType: r.documentType,
      label: r.label,
      prefix: r.prefix,
      padding: r.padding as PaddingValue,
      nextSeq: r.nextSeq,
      poolId: r.poolId,
      canSetNextSeq: r.canSetNextSeq,
    };
  }
  return out;
}

/** Card is dirty when prefix or padding diverges from baseline.
 *  `nextSeq` is excluded — it has its own mutation flow. */
export function isCardDirty(card: NumberingCardValues, base: NumberingCardValues): boolean {
  return card.prefix !== base.prefix || card.padding !== base.padding;
}

/** Any card in the state is dirty. */
export function anyCardDirty(state: NumberingFormState, base: NumberingFormState): boolean {
  for (const dt of Object.keys(state)) {
    const card = state[dt];
    const baseline = base[dt];
    if (card && baseline && isCardDirty(card, baseline)) return true;
  }
  return false;
}

/** List the doc types that need a save. Drives the multi-call save flow. */
export function dirtyDocumentTypes(
  state: NumberingFormState,
  base: NumberingFormState,
): string[] {
  return Object.keys(state).filter((dt) => {
    const card = state[dt];
    const baseline = base[dt];
    return card && baseline && isCardDirty(card, baseline);
  });
}

/** Format a preview number using the live (unsaved) state. */
export function formatPreview(prefix: string, fy: string, seq: number, padding: number): string {
  const safePrefix = prefix.trim() || "—";
  return `${safePrefix}/${fy}/${String(seq).padStart(padding, "0")}`;
}
